import { Heading, Text, Grid, useTheme } from '@simplywallst/ui-core';

import { formatterCurrency } from '@/utilities/format';
import {
  formatCurrencyTruncatedWithSign,
  formatPercentageWithSign,
} from '@/utilities/portfolio/formatValue';

import PositiveNegativeValue from '@/components/PositiveNegativeValue';

import type { Portfolio } from '@/hooks/usePortfolio';

import { useManualPortfolioContext } from '@/components/PortfolioManual';

import Tooltip from '@/components/Tooltip';

import { ManualPortfolioHoldingCompletition } from './components/ManualPortfolioHoldingCompletition';
import { TotalValueDesktop } from './components/TotalValueDesktop';

import { InformationIcon, TooltipTextWrapper } from './styled';

interface Props {
  portfolio: Portfolio;
}

export const SummaryDesktop = ({ portfolio }: Props) => {
  const theme = useTheme();
  const { updatePortfolio } = useManualPortfolioContext();
  const { analysis, currencyISO, holdings, type } = portfolio;
  return (
    <Grid
      as="section"
      gap="16px"
      templateRows="min-content min-content min-content min-content min-content"
    >
      <Grid
        alignItems="center"
        gap="4px"
        templateColumns="max-content min-content"
      >
        <Heading
          as="h2"
          typography="baseStrong"
          opacity={0.5}
          color="whiteRGBA"
        >
          Summary
        </Heading>
        <Tooltip
          title={
            <TooltipTextWrapper gap="8px">
              <Text typography="tiny" color="spaceGray">
                Total Value is the sum of the current value of your holdings.
              </Text>
              <Text typography="tiny" color="spaceGray">
                Total Returns represent the unrealised profits or losses for
                your current holdings. Realised gains or losses from fully or
                partially liquidated stocks are not considered.
              </Text>
              <Text typography="tiny" color="spaceGray">
                Amount invested refers to the initial capital used to purchase
                the current holdings.
              </Text>
            </TooltipTextWrapper>
          }
          light
          placement="top"
          zIndex={99999}
        >
          <Grid>
            <InformationIcon fill={theme.color.whiteRGBA(0.5)} />
          </Grid>
        </Tooltip>
      </Grid>
      <Grid gap="2px">
        <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
          Total Value
        </Text>
        <TotalValueDesktop value={analysis.value} currencyISO={currencyISO} />
      </Grid>
      <Grid gap="2px">
        <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
          Total Returns
        </Text>
        <Grid gap="8px" justifyContent="start" templateColumns="auto auto auto">
          <Text typography="smallStrong">
            <PositiveNegativeValue value={analysis.roiValue}>
              {formatCurrencyTruncatedWithSign(analysis.roiValue, currencyISO)}
            </PositiveNegativeValue>
          </Text>
          {analysis.roi > 0 && (
            <>
              <Text
                as="span"
                typography="smallStrong"
                color="whiteRGBA"
                opacity={0.3}
              >
                |
              </Text>
              <Grid templateColumns="auto auto" gap="2px" alignItems="center">
                <Text typography="smallStrong">
                  <PositiveNegativeValue value={analysis.roi}>
                    {formatPercentageWithSign(analysis.roi)}
                  </PositiveNegativeValue>
                </Text>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
      <Grid gap="2px">
        <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
          Amount Invested
        </Text>
        <Text
          typography="smallStrong"
          color="whiteRGBA"
          opacity={analysis.capitalInvested === null ? 0.3 : 0.5}
        >
          {formatterCurrency(analysis.capitalInvested, currencyISO)}
        </Text>
      </Grid>
      {type === 'manual' && (
        <ManualPortfolioHoldingCompletition
          holdings={holdings}
          onClick={() => updatePortfolio({ portfolioId: portfolio.id })}
        />
      )}
    </Grid>
  );
};
